import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { VngButtonComponent, VngInputComponent } from "@vengreso/angular-components";
import { ClipboardService } from "ngx-clipboard";

/**
 * The read-only invite-link field with its "Copy Link" suffix button and the
 * transient "Link copied!" tooltip — the block that was byte-identical in
 * fly-cut-share and fly-cut-invite.
 *
 * This component OWNS the clipboard copy (ngx-clipboard) and the 2-second
 * tooltip state. Host-specific side effects stay at the host, wired to
 * `(copied)`:
 * - fly-cut-share fires its one-time `v1/flyshares/clone/<token>` POST,
 * - fly-cut-invite shows its "Link copied successfully!" toast.
 *
 * Sizing lives in this component's own stylesheet (M3): it stretches to fill
 * its host column. That rule used to be a fly-cut-invite-local override, which
 * left fly-cut-share's copy of the same field rendering at ~half width.
 */
@Component({
  selector: "app-copy-link-field",
  standalone: true,
  imports: [CommonModule, FormsModule, VngInputComponent, VngButtonComponent],
  templateUrl: "./copy-link-field.component.html",
  styleUrls: ["./copy-link-field.component.scss"],
})
export class CopyLinkFieldComponent {
  /** The link shown in the disabled input and written to the clipboard. */
  @Input() value = "";

  /** Emits the copied link AFTER a successful copy (never fires while `value` is still empty). */
  @Output() copied = new EventEmitter<string>();

  /** Drives the 2s "Link copied!" tooltip. */
  showToolTip = false;

  constructor(private readonly clipboardService: ClipboardService) {}

  copyLink(): void {
    if (!this.value) {
      return;
    }

    this.clipboardService.copyFromContent(this.value);

    this.showToolTip = true;
    setTimeout(() => (this.showToolTip = false), 2000);

    this.copied.emit(this.value);
  }
}
